import { useEffect, useRef, useState } from 'react';
import { blogGenRun } from '../../../services/api';

export const ACTIVE = new Set(['queued', 'running']);

export const errMsg = (err, fallback = 'Something went wrong') =>
  err?.response?.data?.detail || err?.message || fallback;

export const fmtUsd = (v) => (v == null ? '–' : `$${Number(v).toFixed(3)}`);
export const fmtMs = (v) => (v == null ? '–' : v < 1000 ? `${v} ms` : `${(v / 1000).toFixed(0)} s`);
export const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '');

/** mm:ss / [mm:ss] / h:mm:ss → seconds */
export const tsToSeconds = (ts) => {
  if (!ts) return 0;
  const parts = String(ts).replace(/[[\]]/g, '').split(':').map((n) => parseInt(n, 10) || 0);
  return parts.reduce((acc, n) => acc * 60 + n, 0);
};

export const youtubeAt = (videoId, ts) =>
  `https://www.youtube.com/watch?v=${videoId}&t=${tsToSeconds(ts)}s`;

/** Poll a run until it finishes. onDone(run) fires once per run id. */
export function useRunPoller(runId, onDone, intervalMs = 2500) {
  const [run, setRun] = useState(null);
  const doneRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!runId) {
      setRun(null);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const r = await blogGenRun(runId);
        if (cancelled) return;
        setRun(r);
        if (ACTIVE.has(r.status)) {
          timer = setTimeout(tick, intervalMs);
        } else if (doneRef.current !== runId) {
          doneRef.current = runId;
          onDoneRef.current?.(r);
        }
      } catch (e) {
        if (!cancelled) timer = setTimeout(tick, intervalMs * 2);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, intervalMs]);

  return run;
}

/** Debounced callback that always calls the latest fn. */
export function useDebouncedCallback(fn, delay = 600) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timer = useRef(null);
  return (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fnRef.current(...args), delay);
  };
}
